import React, { useState, useRef, useEffect } from "react";
import { Bot, X, Send, Sparkles } from "lucide-react";
import { aiWelcome, aiSuggestions } from "../mock";
import { sendChat } from "../api";

const sessionId = "web-" + Math.random().toString(36).slice(2, 11);

const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState([{ id: "welcome", role: "assistant", text: aiWelcome }]);
  const endRef = useRef(null);

  const mkId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content) return;
    setMessages((m) => [...m, { id: mkId(), role: "user", text: content }]);
    setInput("");
    setTyping(true);
    try {
      const res = await sendChat(sessionId, content);
      setMessages((m) => [...m, { id: mkId(), role: "assistant", text: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { id: mkId(), role: "assistant", text: "Désolé, une erreur est survenue. Contactez-nous à contact@ptitservices-pro.com." },
      ]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-6 left-6 z-40 flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold pl-4 pr-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all ${
          open ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        <Bot size={20} />
        Assistant IA
        <Sparkles size={15} className="text-white/80" />
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 left-6 z-50 w-[92vw] max-w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden animate-fade-up">
          <div className="bg-brand text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-white/20 rounded-full p-1.5">
                <Bot size={18} />
              </div>
              <div>
                <p className="font-heading font-bold text-sm leading-none">Assistant PTITSERVICES-PRO</p>
                <p className="text-[11px] text-white/70 mt-1">En ligne · réponse instantanée</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-neutral-50">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-brand text-white rounded-br-sm"
                      : "bg-white border border-neutral-200 text-neutral-800 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-white border border-neutral-200 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-neutral-300 rounded-full animate-bounce" />
                    <span className="w-2 h-2 bg-neutral-300 rounded-full animate-bounce [animation-delay:0.15s]" />
                    <span className="w-2 h-2 bg-neutral-300 rounded-full animate-bounce [animation-delay:0.3s]" />
                  </div>
                </div>
              </div>
            )}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {aiSuggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-xs text-brand bg-brand/5 border border-brand/20 px-3 py-1.5 rounded-full hover:bg-brand/10 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="border-t border-neutral-200 p-3 flex items-center gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Écrivez votre message..."
              className="flex-1 bg-neutral-100 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/20"
            />
            <button
              onClick={() => send()}
              className="bg-brand hover:bg-brand-dark text-white p-2.5 rounded-full transition-colors"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIAssistant;
